import { Environment, Lightformer } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import type { CubeTexture } from "three";
import { updateReflectionMap } from "../editor3d/ReflectionMap";

interface StandardEnvironmentProps {
  /**
   * Auflösung der Environment Map
   * @default 512
   */
  resolution?: number;

  /**
   * Legt fest, ob die Environment-Map als globale Ressource für die Szene verwendet werden soll
   * @default true
   */
  isGlobal?: boolean;
}

/**
 * Eine standardisierte, wiederverwendbare Environment Map für alle 3D-Renderer
 * Stellt eine konsistente Beleuchtungsumgebung für alle Produktansichten bereit
 */
export const StandardEnvironment = ({
  resolution = 512,
  isGlobal = true,
}: StandardEnvironmentProps) => {
  const { scene } = useThree();
  const envMapRef = useRef<CubeTexture | null>(null);

  // Synchronisiere die generierte Environment mit der globalen ReflectionMap
  useEffect(() => {
    // Aufräumen, wenn die Komponente entfernt wird
    return () => {
      if (isGlobal && envMapRef.current) {
        scene.environment = null;
      }
    };
  }, [isGlobal, scene]);

  // Handler für die Environment-Map-Erstellung
  const handleEnvironmentCreated = (envMap: CubeTexture) => {
    if (isGlobal) {
      // Speichern zur späteren Referenz
      envMapRef.current = envMap;

      // Globale Szenen-Umgebung setzen (wirkt auf MeshStandardMaterial)
      scene.environment = envMap;

      // Die ReflectionMap aktualisieren für bessere Kompatibilität mit älteren Materialien
      updateReflectionMap(envMap);
    }
  };

  return (
    <Environment
      resolution={resolution}
      // Verbinde die Environment Map mit der globalen Szene für alle Materialien
      scene={isGlobal ? scene : undefined}
      // Die props 'onCreated' ist nicht in der TypeScript-Definition, also übergeben wir sie als zusätzliches Attribut
      {...{ onCreated: handleEnvironmentCreated }}
    >
      {/* Ceiling */}
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, -9]}
        scale={[10, 1, 1]}
      />
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, -6]}
        scale={[10, 1, 1]}
      />
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, -3]}
        scale={[10, 1, 1]}
      />
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, 0]}
        scale={[10, 1, 1]}
      />
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, 3]}
        scale={[10, 1, 1]}
      />
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, 6]}
        scale={[10, 1, 1]}
      />
      <Lightformer
        intensity={1.3}
        rotation-x={Math.PI / 2}
        position={[0, 4, 9]}
        scale={[10, 1, 1]}
      />
      {/* Sides */}
      <Lightformer
        intensity={1.35}
        rotation-y={Math.PI / 2}
        position={[-50, 2, 0]}
        scale={[100, 2, 1]}
      />
      <Lightformer
        intensity={1.35}
        rotation-y={-Math.PI / 2}
        position={[50, 2, 0]}
        scale={[100, 2, 1]}
      />
      {/* Front and Back */}
      <Lightformer
        intensity={1.2}
        rotation-y={0}
        position={[0, 2, 50]}
        scale={[100, 2, 1]}
      />
      <Lightformer
        intensity={1.2}
        rotation-y={Math.PI}
        position={[0, 2, -50]}
        scale={[100, 2, 1]}
      />
      {/* Bottom Light */}
      <Lightformer
        intensity={0.9}
        rotation-x={-Math.PI / 2}
        position={[0, -4, 0]}
        scale={[10, 10, 1]}
      />
      {/* Key Light — toned down to avoid harsh aluminum specular on frontal views */}
      <Lightformer
        form="ring"
        color="red"
        intensity={2}
        scale={2}
        position={[10, 5, 10]}
        onUpdate={(self) => self.lookAt(0, 0, 0)}
      />
    </Environment>
  );
};
